import React from 'react';
import type { ButtonProps, ButtonVariant } from './type';
import { css, cx } from '@mildang/styled-system/css';
import { buttonV2, buttonBase } from '@mildang/styled-system/recipes';
import { splitCssProps } from '@mildang/styled-system/jsx';
import { migrateSize } from '../../lib/migrateSize';
import { ButtonBase, iconColorClass, iconColorCustomProperty, type SpinnerColor } from '../_shared';
import { VARIANT_TO_PADDING } from './buttonVariant';

const LOADING_SIZE_MAP = {
  xl: 24,
  lg: 22,
  md: 20,
  xs: 18,
  sm: 18,
} as const;

function getSpinnerSize(size: ButtonProps['size']) {
  const key = (size || 'md') as keyof typeof LOADING_SIZE_MAP;
  return LOADING_SIZE_MAP[key] ?? 20;
}

const FILLED_VARIANTS = new Set<ButtonVariant>(['impact', 'primary']);

function getSpinnerColor(variant: ButtonVariant): SpinnerColor {
  return FILLED_VARIANTS.has(variant) ? 'current' : 'neutral';
}

// `iconColor` 를 CSS 변수 한 겹으로 소비하는 이유·방법은 `_shared/iconColorStyle.ts` 참고.
// Button/ToggleButton 이 이 헬퍼를 공유한다 — 각자 `iconColor` 를 top-level destructuring 으로
// 반드시 소비하고 `{...restProps}` 아래로 흘려보내지 않아야 한다 (안 그러면 DOM 까지 샌다).

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, forwardedRef) => {
  const {
    children,
    className,
    endIcon,
    fullWidth = false,
    size = 'md',
    startIcon,
    variant = 'quaternary',
    loading = false,
    disabled = false,
    sx,
    asChild = false,
    type = 'button',
    iconColor,
    ...other
  } = props;

  const [cssProps, restProps] = splitCssProps(other);
  const { css: _cssStyleProps, ...styleProps } = cssProps;
  const _size = migrateSize(size);
  const padding = VARIANT_TO_PADDING[variant];
  const classes = buttonBase({ padding, size: _size, fullWidth });
  const colorClasses = buttonV2({ variant });
  const iconColorStyle = iconColorCustomProperty(iconColor);

  return (
    <ButtonBase
      ref={forwardedRef}
      asChild={asChild}
      type={type}
      disabled={disabled}
      loading={loading}
      loadingColor={getSpinnerColor(variant)}
      loadingSize={getSpinnerSize(_size)}
      startIcon={startIcon}
      endIcon={endIcon}
      classes={{
        ...classes,
        root: cx(classes.root, colorClasses.root),
        icon: cx(classes.icon, colorClasses.icon),
      }}
      className={cx(iconColor ? iconColorClass : undefined, css(styleProps, sx), className)}
      {...restProps}
      style={iconColorStyle ? { ...iconColorStyle, ...restProps.style } : restProps.style}
    >
      {children}
    </ButtonBase>
  );
});

Button.displayName = 'Button';
